import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  OutlinedInput,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
  type SelectChangeEvent,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { generateAssetSectors } from "../data/assetSubSectors";
import { generateAssetTypes } from "../data/assetTypes";
import { generateClassificationTrees } from "../data/classificationTrees";
import type { Entity, EntityGroup, ValuationAsset } from "../types/valuation";
import { groupAssets, type GroupedData } from "../utils/grouping";
import FilterSelector, { type FilterConfig } from "./FilterSelector";
import GroupingSelector, { type GroupingConfig } from "./GroupingSelector";

interface ValuationTableProps {
  assets: ValuationAsset[];
  entities: Entity[];
  entityGroup: EntityGroup;
  totalValue: number;
}

const formatCurrency = (value: number, currency = "USD"): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency === "GBP" ? "GBP" : currency === "EUR" ? "EUR" : "USD",
    minimumFractionDigits: 2,
  }).format(value);
};

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatPercentage = (value: number): string => {
  return `${value.toFixed(2)}%`;
};

const ValuationTableV2: React.FC<ValuationTableProps> = ({
  assets,
  entities,
  entityGroup,
  totalValue,
}) => {
  const [selectedGroupings, setSelectedGroupings] = useState<GroupingConfig[]>([
    { id: "entity", label: "Entity", type: "entity" },
  ]);
  const [filters, setFilters] = useState<FilterConfig>({
    entities: [],
    assetClasses: [],
    sectors: [],
    portfolios: [],
    classificationTree: null,
    asAtDate: new Date(),
    reportable: "reportable",
  });
  const [hoveredSlice, setHoveredSlice] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<number>(0);

  // Asset allocation chart controls
  const [allocationViewType, setAllocationViewType] = useState<
    "sectors" | "subsectors"
  >("sectors");
  const [selectedSector, setSelectedSector] = useState<string>("");

  // Menu state for asset actions
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [assetsData, setAssetsData] = useState<ValuationAsset[]>(assets);

  // Create lookup maps for asset types and sub sectors
  const assetTypes = useMemo(() => generateAssetTypes(), []);
  const assetSubSectors = useMemo(() => generateAssetSectors(), []);

  const assetTypeMap = useMemo(() => {
    const map = new Map<number, string>();
    assetTypes.forEach((type) => {
      map.set(type.id, type.name);
    });
    return map;
  }, [assetTypes]);

  const assetSubSectorMap = useMemo(() => {
    const map = new Map<number, string>();
    assetSubSectors.forEach((subSector) => {
      map.set(subSector.id, subSector.name);
    });
    return map;
  }, [assetSubSectors]);

  // Create sector mapping based on sectorId
  const sectorMap = useMemo(
    () =>
      new Map<number, string>([
        [1, "Alternative Investments"],
        [2, "Equities"],
        [3, "Fixed Income"],
        [4, "Cash & Cash Equivalents"],
        [5, "Real Estate"],
        [6, "Diversified Funds"],
        [7, "Digital Assets"],
        [8, "Commodities"],
        [9, "Other"],
        [10, "Tangible Assets"],
        [11, "Private Markets"],
      ]),
    [],
  );

  // Create entity name mapping
  const entityNameMap = useMemo(() => {
    const map = new Map<string, string>();
    entities.forEach((entity) => {
      map.set(entity.id, entity.name);
    });
    return map;
  }, [entities]);

  // Apply filters to assets
  const filteredAssets = useMemo(() => {
    return assetsData.filter((asset) => {
      // Entity filter
      if (
        filters.entities.length > 0 &&
        !filters.entities.includes(asset.entityId)
      ) {
        return false;
      }

      // Asset type filter
      if (filters.assetClasses.length > 0) {
        const assetTypeName = asset.assetTypeId
          ? assetTypeMap.get(asset.assetTypeId) || "Unknown"
          : "Unknown";
        if (!filters.assetClasses.includes(assetTypeName)) {
          return false;
        }
      }

      // Sector filter
      if (filters.sectors.length > 0) {
        const subSectorName = asset.assetSubSectorId
          ? assetSubSectorMap.get(asset.assetSubSectorId) || "Unknown"
          : "Unknown";
        if (!filters.sectors.includes(subSectorName)) {
          return false;
        }
      }

      // Portfolio filter
      if (
        filters.portfolios.length > 0 &&
        !filters.portfolios.includes(asset.portfolioName)
      ) {
        return false;
      }

      // Classification tree filter
      if (
        filters.classificationTree &&
        filters.classificationTree.itemIds.length > 0
      ) {
        const trees = generateClassificationTrees();
        const selectedTree = trees.find(
          (tree) => tree.id === filters.classificationTree!.treeId,
        );

        if (selectedTree && selectedTree.items) {
          let treeMatch = false;

          // Check if asset is in any of the selected classification items
          for (const itemId of filters.classificationTree.itemIds) {
            const treeItem = selectedTree.items.find(
              (item) => item.id === itemId,
            );
            if (
              treeItem &&
              treeItem.assetIds &&
              treeItem.assetIds.includes(asset.id)
            ) {
              treeMatch = true;
              break;
            }
          }

          if (!treeMatch) {
            return false;
          }
        }
      }

      // Reportable filter
      if (filters.reportable === "reportable" && !asset.reportable) {
        return false;
      }
      if (filters.reportable === "non-reportable" && asset.reportable) {
        return false;
      }

      return true;
    });
  }, [assetsData, filters, assetTypeMap, assetSubSectorMap]);

  // Calculate filtered total value
  const filteredTotalValue = useMemo(() => {
    return filteredAssets.reduce(
      (sum, asset) => sum + asset.marketValueReporting,
      0,
    );
  }, [filteredAssets]);

  // Group filtered assets based on selected groupings
  const groupedAssets = useMemo(() => {
    return groupAssets(filteredAssets, selectedGroupings, entityNameMap);
  }, [filteredAssets, selectedGroupings, entityNameMap]);

  // Calculate allocations for the pie chart based on allocation view type
  const allocationData = useMemo(() => {
    if (allocationViewType === "sectors") {
      // Group by sectors
      const allocations = new Map<string, number>();

      filteredAssets.forEach((asset) => {
        if (asset.assetSubSectorId) {
          const subSector = assetSubSectors.find(
            (s) => s.id === asset.assetSubSectorId,
          );
          if (subSector) {
            const sectorName =
              sectorMap.get(subSector.sectorId) || "Unknown Sector";
            allocations.set(
              sectorName,
              (allocations.get(sectorName) || 0) + asset.marketValueReporting,
            );
          }
        }
      });

      return Array.from(allocations.entries())
        .map(([name, value]) => ({
          name,
          value,
          percentage: (value / filteredTotalValue) * 100,
        }))
        .sort((a, b) => b.value - a.value);
    } else {
      // Group by subsectors - either all or filtered by selected sector
      const allocations = new Map<string, number>();

      filteredAssets.forEach((asset) => {
        if (asset.assetSubSectorId) {
          const subSector = assetSubSectors.find(
            (s) => s.id === asset.assetSubSectorId,
          );
          if (subSector) {
            // If a sector is selected, only show subsectors from that sector
            if (selectedSector) {
              const sectorName = sectorMap.get(subSector.sectorId);
              if (sectorName !== selectedSector) {
                return; // Skip this asset if it's not in the selected sector
              }
            }
            allocations.set(
              subSector.name,
              (allocations.get(subSector.name) || 0) +
                asset.marketValueReporting,
            );
          }
        }
      });

      return Array.from(allocations.entries())
        .map(([name, value]) => ({
          name,
          value,
          percentage: (value / filteredTotalValue) * 100,
        }))
        .sort((a, b) => b.value - a.value);
    }
  }, [
    filteredAssets,
    filteredTotalValue,
    allocationViewType,
    selectedSector,
    assetSubSectors,
    sectorMap,
  ]);

  // Get available sectors for the selector
  const availableSectors = useMemo(() => {
    const sectorNames = new Set<string>();
    filteredAssets.forEach((asset) => {
      if (asset.assetSubSectorId) {
        const subSector = assetSubSectors.find(
          (s) => s.id === asset.assetSubSectorId,
        );
        if (subSector) {
          const sectorName = sectorMap.get(subSector.sectorId);
          if (sectorName) {
            sectorNames.add(sectorName);
          }
        }
      }
    });
    return Array.from(sectorNames).sort();
  }, [filteredAssets, assetSubSectors, sectorMap]);

  // Get the display label for the chart
  const chartLabel = useMemo(() => {
    if (allocationViewType === "sectors") {
      return "Sectors";
    } else {
      return selectedSector ? `${selectedSector} Subsectors` : "All Subsectors";
    }
  }, [allocationViewType, selectedSector]);

  // Colors for pie chart
  const COLORS = [
    "#1976d2",
    "#dc004e",
    "#ed6c02",
    "#2e7d32",
    "#9c27b0",
    "#d32f2f",
    "#7b1fa2",
    "#303f9f",
  ];

  const shouldShowAllocations = useMemo(() => {
    return allocationData.length > 0;
  }, [allocationData]);

  // Menu handlers for asset actions
  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    assetId: string,
  ) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedAssetId(assetId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedAssetId(null);
  };

  const handleToggleReportable = () => {
    if (selectedAssetId) {
      setAssetsData((prevAssets) =>
        prevAssets.map((asset) =>
          asset.id.toString() === selectedAssetId
            ? { ...asset, reportable: !asset.reportable }
            : asset,
        ),
      );
    }
    handleMenuClose();
  };

  // Flatten grouped data into a single table with group headers
  const renderFlatTable = (
    groups: GroupedData[],
    level = 0,
  ): React.ReactElement[] => {
    const rows: React.ReactElement[] = [];

    groups.forEach((group) => {
      // Add group header row
      rows.push(
        <TableRow
          key={`group-${level}-${group.key}`}
          sx={{
            bgcolor: level === 0 ? "primary.light" : "action.hover",
            "& td": {
              fontWeight: "bold",
              color: level === 0 ? "primary.contrastText" : "text.primary",
            },
          }}
        >
          <TableCell
            colSpan={11}
            sx={{
              pl: level * 3 + 2,
              fontWeight: "bold",
              fontSize: "1rem",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                {group.label} ({group.count} assets -{" "}
                {formatCurrency(group.total)})
              </Box>
              <Chip
                label={group.count}
                size="small"
                variant="outlined"
                sx={{ fontSize: "0.75rem", fontWeight: "medium" }}
              />
            </Box>
          </TableCell>
        </TableRow>,
      );

      if (group.subGroups && group.subGroups.length > 0) {
        // Recursively add sub-groups
        rows.push(...renderFlatTable(group.subGroups, level + 1));
      } else {
        // Add asset rows
        group.assets.forEach((asset) => {
          rows.push(
            <TableRow key={asset.id} hover>
              <TableCell sx={{ width: "20%", pl: (level + 1) * 3 + 2 }}>
                <Typography variant="body2" fontWeight="medium">
                  {asset.assetName}
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {asset.assetTypeId
                      ? assetTypeMap.get(asset.assetTypeId) || "Unknown"
                      : "Unknown"}{" "}
                    •{" "}
                    {asset.assetSubSectorId
                      ? assetSubSectorMap.get(asset.assetSubSectorId) ||
                        "Unknown"
                      : "Unknown"}
                  </Typography>
                </Box>
              </TableCell>

              <TableCell sx={{ width: "12%" }}>
                {asset.investmentCode ? (
                  <Box>
                    <Typography
                      variant="body2"
                      component="div"
                      fontWeight="medium"
                    >
                      {asset.investmentCode}
                    </Typography>
                    {asset.exchange && (
                      <Chip
                        label={asset.exchange}
                        size="small"
                        variant="outlined"
                        sx={{ mt: 0.5 }}
                      />
                    )}
                  </Box>
                ) : (
                  "-"
                )}
              </TableCell>

              <TableCell align="right" sx={{ width: "8%" }}>
                {asset.isUnitised && asset.unitsHeld
                  ? new Intl.NumberFormat().format(asset.unitsHeld)
                  : "-"}
              </TableCell>

              <TableCell align="right" sx={{ width: "8%" }}>
                {asset.isUnitised && asset.unitPrice
                  ? formatCurrency(asset.unitPrice, asset.priceBaseCurrency)
                  : "-"}
              </TableCell>

              <TableCell sx={{ width: "6%" }}>
                <Typography variant="body2" fontWeight="medium">
                  {asset.priceBaseCurrency}
                </Typography>
              </TableCell>

              <TableCell sx={{ width: "8%" }}>
                {asset.isUnitised && asset.priceDate
                  ? formatDate(asset.priceDate)
                  : "-"}
              </TableCell>

              <TableCell align="right" sx={{ width: "9%" }}>
                {asset.pendingSettlement
                  ? formatCurrency(
                      asset.pendingSettlement,
                      asset.priceBaseCurrency,
                    )
                  : "-"}
              </TableCell>

              <TableCell align="right" sx={{ width: "9%" }}>
                {asset.accruedIncome
                  ? formatCurrency(asset.accruedIncome, asset.priceBaseCurrency)
                  : "-"}
              </TableCell>

              <TableCell align="right" sx={{ width: "12%" }}>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color="primary.main"
                >
                  {formatCurrency(asset.marketValueReporting)}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block" }}
                >
                  {formatCurrency(
                    asset.marketValueDomicile,
                    asset.priceBaseCurrency,
                  )}{" "}
                  ({asset.priceBaseCurrency})
                </Typography>
              </TableCell>

              <TableCell align="right" sx={{ width: "8%" }}>
                <Typography variant="body2" color="text.secondary">
                  {formatPercentage(asset.percentAllocation)}
                </Typography>
              </TableCell>

              <TableCell align="center" sx={{ width: "6%" }}>
                <IconButton
                  size="small"
                  onClick={(event) =>
                    handleMenuOpen(event, asset.id.toString())
                  }
                  aria-label="asset actions"
                >
                  <MoreVertIcon />
                </IconButton>
              </TableCell>
            </TableRow>,
          );
        });
      }
    });

    return rows;
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ textAlign: "center", mb: 3 }}>
        <Typography variant="h4" gutterBottom color="primary.main">
          Portfolio Valuation Report (Version 2)
        </Typography>
        <Typography
          variant="h5"
          gutterBottom
          color="text.secondary"
          sx={{ fontWeight: "medium" }}
        >
          {entityGroup.name}
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" color="text.secondary">
          Total Portfolio Value: <strong>{formatCurrency(totalValue)}</strong>
        </Typography>
        {filteredTotalValue !== totalValue && (
          <Typography
            variant="body2"
            color="primary.main"
            sx={{ fontWeight: "medium" }}
          >
            Filtered Value:{" "}
            <strong>{formatCurrency(filteredTotalValue)}</strong>(
            {formatPercentage((filteredTotalValue / totalValue) * 100)} of
            total)
          </Typography>
        )}
      </Box>

      {/* Filter / Group Section */}
      <Accordion sx={{ mb: 4 }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            bgcolor: "grey.50",
            "&.Mui-expanded": {
              minHeight: 48,
            },
            "& .MuiAccordionSummary-content": {
              "&.Mui-expanded": {
                margin: "12px 0",
              },
            },
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: "text.primary",
              fontWeight: "bold",
            }}
          >
            Filter / Group
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3, bgcolor: "grey.50" }}>
          <FilterSelector
            assets={assets}
            entities={entities}
            filters={filters}
            onFiltersChange={setFilters}
          />

          <GroupingSelector
            selectedGroupings={selectedGroupings}
            onGroupingsChange={setSelectedGroupings}
          />
        </AccordionDetails>
      </Accordion>

      {/* Tabs for switching between views */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={selectedTab}
          onChange={(_, newValue) => setSelectedTab(newValue)}
        >
          <Tab label="Valuation Table" />
          <Tab label="Asset Allocation" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {selectedTab === 0 && (
        <Box>
          <TableContainer
            component={Paper}
            sx={{
              maxHeight: "70vh",
              overflow: "auto",
            }}
          >
            <Table sx={{ tableLayout: "fixed" }}>
              <TableHead
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 1100,
                  backgroundColor: "grey.100",
                }}
              >
                <TableRow sx={{ bgcolor: "grey.100" }}>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      fontSize: "0.875rem",
                      width: "20%",
                      bgcolor: "grey.100",
                    }}
                  >
                    Asset
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      fontSize: "0.875rem",
                      width: "12%",
                      bgcolor: "grey.100",
                    }}
                  >
                    Code/ Exchange
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "bold",
                      fontSize: "0.875rem",
                      width: "8%",
                      bgcolor: "grey.100",
                    }}
                  >
                    Units
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "bold",
                      fontSize: "0.875rem",
                      width: "8%",
                      bgcolor: "grey.100",
                    }}
                  >
                    Price
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      fontSize: "0.875rem",
                      width: "6%",
                      bgcolor: "grey.100",
                    }}
                  >
                    Currency
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      fontSize: "0.875rem",
                      width: "8%",
                      bgcolor: "grey.100",
                    }}
                  >
                    Date
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "bold",
                      fontSize: "0.875rem",
                      width: "9%",
                      bgcolor: "grey.100",
                    }}
                  >
                    Pending Settlement
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "bold",
                      fontSize: "0.875rem",
                      width: "9%",
                      bgcolor: "grey.100",
                    }}
                  >
                    Accrued Income
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "bold",
                      fontSize: "0.875rem",
                      width: "12%",
                      bgcolor: "grey.100",
                    }}
                  >
                    Value
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "bold",
                      fontSize: "0.875rem",
                      width: "8%",
                      bgcolor: "grey.100",
                    }}
                  >
                    Allocation %
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      fontSize: "0.875rem",
                      width: "6%",
                      bgcolor: "grey.100",
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{renderFlatTable(groupedAssets)}</TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {selectedTab === 1 && (
        <Box>
          {/* Allocation Controls */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Asset Allocation Settings
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: 3,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              {/* View Type Selector */}
              <FormControl>
                <RadioGroup
                  row
                  value={allocationViewType}
                  onChange={(e) => {
                    setAllocationViewType(
                      e.target.value as "sectors" | "subsectors",
                    );
                    // Reset sector selection when changing view type
                    if (e.target.value === "sectors") {
                      setSelectedSector("");
                    }
                  }}
                >
                  <FormControlLabel
                    value="sectors"
                    control={<Radio />}
                    label="Sectors"
                  />
                  <FormControlLabel
                    value="subsectors"
                    control={<Radio />}
                    label="Subsectors"
                  />
                </RadioGroup>
              </FormControl>

              {/* Sector Selector (only when viewing subsectors) */}
              {allocationViewType === "subsectors" && (
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Sector Filter</InputLabel>
                  <Select
                    value={selectedSector}
                    onChange={(e: SelectChangeEvent) =>
                      setSelectedSector(e.target.value)
                    }
                    input={<OutlinedInput label="Sector Filter" />}
                  >
                    <MenuItem value="">
                      <em>All Sectors</em>
                    </MenuItem>
                    {availableSectors.map((sector) => (
                      <MenuItem key={sector} value={sector}>
                        {sector}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          </Paper>

          {/* Allocation Chart and Table */}
          {shouldShowAllocations && allocationData.length > 0 ? (
            <>
              <Typography variant="h6" gutterBottom>
                Asset Allocation
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                By {chartLabel}
              </Typography>

              <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start" }}>
                {/* Allocation Table */}
                <Box sx={{ flex: 1 }}>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <strong>{chartLabel}</strong>
                          </TableCell>
                          <TableCell align="right">
                            <strong>Value</strong>
                          </TableCell>
                          <TableCell align="right">
                            <strong>%</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {allocationData.map((item, index) => (
                          <TableRow
                            key={item.name}
                            onMouseEnter={() => setHoveredSlice(item.name)}
                            onMouseLeave={() => setHoveredSlice(null)}
                            sx={{
                              cursor: "pointer",
                              "&:hover": {
                                bgcolor: "action.hover",
                              },
                            }}
                          >
                            <TableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    bgcolor: COLORS[index % COLORS.length],
                                    borderRadius: 1,
                                  }}
                                />
                                {item.name}
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(item.value)}
                            </TableCell>
                            <TableCell align="right">
                              {item.percentage.toFixed(1)}%
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                {/* Pie Chart */}
                <Box sx={{ flex: "0 0 40%", height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={allocationData}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {allocationData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                            stroke={
                              hoveredSlice === entry.name ? "#000" : "none"
                            }
                            strokeWidth={hoveredSlice === entry.name ? 2 : 0}
                            opacity={
                              hoveredSlice === null ||
                              hoveredSlice === entry.name
                                ? 1
                                : 0.6
                            }
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(_value, name, props) => [
                          `${name} (${props.payload.percentage.toFixed(1)}%)`,
                        ]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            </>
          ) : (
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ textAlign: "center", mt: 4 }}
            >
              No allocation data available with current filters
            </Typography>
          )}
        </Box>
      )}

      {/* Asset Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {selectedAssetId &&
          (() => {
            const selectedAsset = assetsData.find(
              (asset) => asset.id.toString() === selectedAssetId,
            );
            return selectedAsset ? (
              <MenuItem onClick={handleToggleReportable}>
                <ListItemIcon>
                  {selectedAsset.reportable ? (
                    <VisibilityOffIcon />
                  ) : (
                    <VisibilityIcon />
                  )}
                </ListItemIcon>
                <ListItemText>
                  {selectedAsset.reportable
                    ? "Mark as Non-Reportable"
                    : "Mark as Reportable"}
                </ListItemText>
              </MenuItem>
            ) : null;
          })()}
      </Menu>
    </Paper>
  );
};

export default ValuationTableV2;
